import { useState } from 'react';
import { supabase } from '@/services/supabase-client';
import { StorageError } from '@supabase/storage-js';
import { MessageAttachment } from '@/features/messages/messages.types';
import { dataProvider } from '@/services/data-provider';
import { useMutation } from '@tanstack/react-query';
import { createMessageAttachment } from '@/features/messages/messages.service';

// Interface for the upload response
interface MessageAttachmentUploadResponse {
  path: string | null;
  url: string | null;
  error: StorageError | null;
  metadata: {
    id?: string;
    file_name: string;
    file_type: string;
    file_size: number;
    file_url: string;
    duration?: number;
  } | null;
}

// Interface for organization context
interface OrganizationContext {
  organizationId: string;
  locationId: string;
  userId: string;
}

// Hook for uploading message attachments
export function useMessageAttachments() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<StorageError | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isCreatingRecord, setIsCreatingRecord] = useState(false);

  // Get the storage bucket name from env
  const BUCKET_NAME = import.meta.env
    .VITE_SUPABASE_MESSAGE_ATTACHMENTS_BUCKET_PUBLIC;

  // Add near the start of the useMessageAttachments function
  const convertAudioToOpusMutation = useMutation({
    mutationFn: async (fileUrl: string) => {
      console.log('MUTATION: Sending request to convert audio:', fileUrl);
      const response = await dataProvider.create('audio/convert-to-opus', {
        data: { file_url: fileUrl },
      });

      // Log the raw response to debug
      console.log('MUTATION: Raw response received:', response);

      // Make sure we're returning the actual data from the response
      if (!response || !response.data) {
        console.error(
          'MUTATION ERROR: Invalid response format from convert-to-opus endpoint:',
          response,
        );
        throw new Error('Invalid response from audio conversion endpoint');
      }

      // Log the data that will be returned
      console.log('MUTATION: Returning data:', response.data);

      return response.data;
    },
  });

  /**
   * Upload a file to Supabase storage and return metadata for the message attachment
   * @param file The file to upload
   * @param conversationId The conversation ID to organize attachments
   * @param context Organization context (org ID, location ID, user ID)
   * @param messageType The type of message (text, audio, image, file)
   * @param duration Optional duration for audio files
   */
  const uploadMessageAttachment = async (
    file: File | Blob,
    conversationId: string,
    context: OrganizationContext,
    messageType: 'text' | 'audio' | 'image' | 'file',
    duration?: number,
  ): Promise<MessageAttachmentUploadResponse> => {
    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      if (!context.userId) {
        throw new Error('User ID is required');
      }

      // Generate a timestamp-based unique name to avoid collisions
      const timestamp = new Date().getTime();
      const fileName =
        file instanceof File
          ? file.name
          : `${messageType}_${timestamp}.${getExtension(messageType)}`;

      // Create a structured path with organization, location, conversation, and user IDs
      const filePath = `org_${context.organizationId}/conversations/loc_${context.locationId}/conv_${conversationId}/${timestamp}_${fileName}`;

      setUploadProgress(10);

      // Upload the file to Supabase
      const { data, error } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true, // Set to true to overwrite existing files
        });

      if (error) {
        console.error('Supabase upload error:', error);
        throw error;
      }

      setUploadProgress(70);

      // Get the public URL for the file
      const {
        data: { publicUrl },
      } = supabase.storage.from(BUCKET_NAME).getPublicUrl(data.path);

      setUploadProgress(100);

      // Determine file type for the attachment
      let fileType = messageType;
      if (file instanceof File) {
        if (file.type.startsWith('image/')) fileType = 'image';
        else if (file.type.startsWith('audio/')) fileType = 'audio';
        else if (file.type === 'application/pdf') fileType = 'file';
        else if (
          file.type ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        )
          fileType = 'file';
        else fileType = 'file';
      }

      // Create metadata for the message attachment
      const metadata = {
        file_name: fileName,
        file_type: fileType,
        file_size: file.size,
        file_url: publicUrl,
        file_path: filePath,
        ...(duration && { duration }),
      };

      return {
        path: data.path,
        url: publicUrl,
        error: null,
        metadata,
      };
    } catch (err) {
      const error = err as StorageError;
      setUploadError(error);
      return {
        path: null,
        url: null,
        error,
        metadata: null,
      };
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Create a message attachment record in the API after file upload
   */
  const createMessageAttachmentRecord = async (
    messageId: string,
    attachmentData: {
      file_url: string;
      file_type: string;
      file_name: string;
      file_size: number;
      duration?: number;
    },
  ): Promise<MessageAttachment> => {
    setIsCreatingRecord(true);

    try {
      // Prepare the data for the API
      const data = {
        message_id: messageId,
        file_type: attachmentData.file_type,
        file_url: attachmentData.file_url,
        file_size: attachmentData.file_size,
        file_name: attachmentData.file_name,
        is_read: false,
        ...(attachmentData.duration && { duration: attachmentData.duration }),
      };

      // Use dataProvider to create the attachment record
      const response = await createMessageAttachment(data);

      // Return the created record
      return response.data;
    } catch (error) {
      console.error('Error creating message attachment record:', error);
      throw error;
    } finally {
      setIsCreatingRecord(false);
    }
  };

  /**
   * Upload file to Supabase and create API record in one operation
   */
  const uploadAndCreateAttachment = async (
    file: File | Blob,
    messageId: string,
    conversationId: string,
    context: OrganizationContext,
    messageType: 'text' | 'audio' | 'image' | 'file',
    duration?: number,
  ): Promise<MessageAttachment> => {
    // First upload to Supabase
    const uploadResult = await uploadMessageAttachment(
      file,
      conversationId,
      context,
      messageType,
      duration,
    );

    if (uploadResult.error || !uploadResult.metadata) {
      throw new Error(
        `Failed to upload file: ${uploadResult.error?.message || 'Unknown error'}`,
      );
    }

    // Then create the attachment record in the API
    return await createMessageAttachmentRecord(messageId, {
      file_url: uploadResult.url!,
      file_type: uploadResult.metadata.file_type,
      file_name: uploadResult.metadata.file_name,
      file_size: uploadResult.metadata.file_size,
      duration: uploadResult.metadata.duration,
    });
  };

  /**
   * Get appropriate file extension based on message type
   */
  const getExtension = (messageType: string): string => {
    switch (messageType) {
      case 'audio':
        return 'wav';
      case 'image':
        return 'png';
      default:
        return 'bin';
    }
  };

  /**
   * Process multiple attachments for a message
   */
  const uploadMultipleAttachmentsForMessage = async (
    files: Array<{
      file: File | Blob;
      type: 'text' | 'audio' | 'image' | 'file';
      duration?: number;
    }>,
    messageId: string,
    conversationId: string,
    context: OrganizationContext,
  ): Promise<MessageAttachment[]> => {
    const results = await Promise.all(
      files.map(({ file, type, duration }) =>
        uploadAndCreateAttachment(
          file,
          messageId,
          conversationId,
          context,
          type,
          duration,
        ),
      ),
    );

    // Filter out any failed uploads
    return results.filter((result) => !!result.id);
  };

  /**
   * Process multiple attachments and prepare for message creation
   * This is used before the message is created (so no messageId yet)
   */
  const uploadMultipleAttachments = async (
    files: Array<{
      file: File | Blob;
      type: 'text' | 'audio' | 'image' | 'file';
      duration?: number;
    }>,
    conversationId: string,
    context: OrganizationContext,
  ): Promise<Partial<MessageAttachment>[]> => {
    setIsUploading(true);
    const uploadedItems: Partial<MessageAttachment>[] = [];

    try {
      let totalProgress = 0;

      // Upload each file sequentially to track progress
      for (let i = 0; i < files.length; i++) {
        const { file, type, duration } = files[i];

        // Calculate progress for this item
        const fileWeight = 100 / files.length;
        const startProgress = totalProgress;

        // Upload to Supabase
        const result = await uploadMessageAttachment(
          file,
          conversationId,
          context,
          type,
          duration,
        );

        if (result.error || !result.metadata) {
          console.error(
            'Failed to upload file:',
            result.error || 'Unknown error',
          );
          continue;
        }

        // Create a partial MessageAttachment object
        const attachment: Partial<MessageAttachment> = {
          file_url: result.url!,
          file_type: result.metadata.file_type,
          file_name: result.metadata.file_name,
          file_size: result.metadata.file_size,
          uploaded_at: new Date().toISOString(),
          is_read: false,
        };

        // Add duration if it exists
        if (duration || result.metadata.duration) {
          attachment.duration_audio = duration || result.metadata.duration;
        }

        uploadedItems.push(attachment);

        // Update total progress
        totalProgress = startProgress + fileWeight;
        setUploadProgress(Math.round(totalProgress));
      }

      return uploadedItems;
    } catch (error) {
      console.error('Error in uploadMultipleAttachments:', error);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Create database records for previously uploaded attachments
   * This should be called after the message is created and we have a message ID
   */
  const createAttachmentRecords = async (
    messageId: string,
    attachments: Partial<MessageAttachment>[],
  ): Promise<MessageAttachment[]> => {
    setIsCreatingRecord(true);
    const createdAttachments: MessageAttachment[] = [];

    try {
      // Create database records for each attachment
      for (const attachment of attachments) {
        if (!attachment.file_url) {
          console.error('Missing file_url in attachment');
          continue;
        }

        // Prepare the data for API call
        const data = {
          message_id: messageId,
          file_type: attachment.file_type || 'file',
          file_url: attachment.file_url,
          file_size: attachment.file_size || 0,
          file_name: attachment.file_name || 'file',
          is_read: attachment.is_read || false,
          ...(attachment.duration_audio && {
            duration_audio: attachment.duration_audio,
          }),
        };

        // Create the record in the database
        const response = await createMessageAttachment(data);

        if (response && response.data) {
          createdAttachments.push(response.data);
        }
      }

      return createdAttachments;
    } catch (error) {
      console.error('Error creating attachment records:', error);
      throw error;
    } finally {
      setIsCreatingRecord(false);
    }
  };

  return {
    uploadMessageAttachment,
    uploadMultipleAttachments,
    uploadMultipleAttachmentsForMessage,
    createMessageAttachmentRecord,
    uploadAndCreateAttachment,
    createAttachmentRecords,
    isUploading,
    isCreatingRecord,
    uploadError,
    uploadProgress,
    isProcessing: isUploading || isCreatingRecord,
    convertAudioToOpus: convertAudioToOpusMutation.mutateAsync,
    isConvertingAudio: convertAudioToOpusMutation.isPending,
    audioConversionError: convertAudioToOpusMutation.error,
  };
}
